"use client";

import { CalendarCheck, Truck, Waves } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HowToStart() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const card1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const card2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const card3 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const polaroid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const wave = useRevealOnScroll<SVGSVGElement>({
    threshold: 0.2
  });
  const steps = [{
    number: "01",
    title: "Submit Specs",
    copy: "Upload your project requirements, material preferences, and quantity. Get a quote in minutes.",
    rotate: "-rotate-2",
    delay: "0ms",
    reveal: card1,
    icon: CalendarCheck
  }, {
    number: "02",
    title: "Precision Engraving",
    copy: "Our team processes your order using industrial-grade laser technology for perfect accuracy.",
    rotate: "rotate-1",
    delay: "80ms",
    reveal: card2,
    icon: Truck
  }, {
    number: "03",
    title: "Quality Delivery",
    copy: "We inspect every piece for quality and ship your finished labels directly to your site.",
    rotate: "-rotate-1",
    delay: "160ms",
    reveal: card3,
    icon: Waves
  }];
  return <section data-section-id="1323" id="how-to-start" className="relative overflow-hidden py-24 md:py-36 bg-background text-foreground">
      {}
      <div className="container mx-auto px-6 md:px-12">
        <div ref={header.ref} className="mx-auto max-w-4xl text-center transition-all duration-700 ease-out" style={{
        opacity: header.isVisible ? 1 : 0,
        transform: header.isVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.18em] mb-6 text-primary">
            § 02 / WORKFLOW / EFFICIENT
          </p>
          <h2 className="font-serif font-default text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.95] tracking-tight text-accent">
            THREE STEPS TO{" "}
            <span className="relative inline-block">
              <span className="text-primary">PRECISION</span>
              <svg className="absolute -bottom-3 left-0 w-full" height="22" viewBox="0 0 200 22" fill="none" preserveAspectRatio="none" aria-hidden="true">
                <path d="M4 14 Q 40 4, 80 12 T 160 10 T 196 14" stroke="currentColor" className="text-primary" strokeWidth="4" strokeLinecap="round" fill="none" style={{
                strokeDasharray: 240,
                strokeDashoffset: header.isVisible ? 0 : 240,
                transition: "stroke-dashoffset 800ms ease-out 300ms"
              }} />
              </svg>
            </span>
          </h2>
        </div>

        {}
        <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {}
          <div className="lg:col-span-8 relative">
            {}
            <svg ref={wave.ref} className="hidden lg:block absolute top-1/2 left-0 w-full -translate-y-1/2 pointer-events-none z-0" height="80" viewBox="0 0 800 80" fill="none" preserveAspectRatio="none" aria-hidden="true">
              <path d="M 60 40 Q 140 10, 220 40 T 380 40 T 540 40 T 720 40" stroke="currentColor" className="text-primary" strokeWidth="3" strokeLinecap="round" strokeDasharray="10 8" fill="none" style={{
              strokeDashoffset: wave.isVisible ? 0 : 800,
              transition: "stroke-dashoffset 1200ms ease-out 600ms"
            }} />
              <polygon points="720,32 740,40 720,48" fill="currentColor" className="text-primary" style={{
              opacity: wave.isVisible ? 1 : 0,
              transition: "opacity 400ms ease-out 1600ms"
            }} />
            </svg>

            <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
              {steps.map((step, i) => {
              const Icon = step.icon;
              return <div key={i} data-index={i} ref={step.reveal.ref} className={`relative rounded-lg p-7 md:p-8 ${step.rotate} hover:rotate-0 transition-all duration-300 ease-out overflow-hidden bg-card border border-border shadow-md`} style={{
                opacity: step.reveal.isVisible ? 1 : 0,
                transform: step.reveal.isVisible ? undefined : "translateY(24px)",
                transitionProperty: "transform, box-shadow, opacity, rotate",
                transitionDelay: step.reveal.isVisible ? step.delay : "0ms",
                transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
                transitionDuration: "600ms"
              }}>
                    {}
                    <span aria-hidden="true" className="absolute -top-6 -right-2 font-serif font-default select-none pointer-events-none leading-none text-muted" style={{
                  fontSize: "12rem",
                  zIndex: 0
                }}>
                      {step.number}
                    </span>

                    <div className="relative z-10">
                      {}
                      <div className="inline-flex items-center justify-center mb-5 rounded-lg bg-primary border border-border shadow-md" style={{
                    width: "64px",
                    height: "64px"
                  }}>
                        <Icon className="w-8 h-8 text-primary-foreground" strokeWidth={2.5} />
                      </div>

                      <p className="font-mono text-xs uppercase tracking-[0.14em] mb-2 text-muted-foreground">
                        Step {step.number}
                      </p>

                      <h3 className="font-serif font-default text-2xl md:text-[1.75rem] leading-[1.05] tracking-tight text-card-foreground mb-3">
                        {step.title}
                      </h3>

                      <p className="font-sans text-base leading-relaxed text-card-foreground">
                        {step.copy}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {}
            <svg className="lg:hidden absolute left-1/2 top-0 -translate-x-1/2 h-full pointer-events-none -z-0" width="40" viewBox="0 0 40 800" fill="none" preserveAspectRatio="none" aria-hidden="true">
              <path d="M 20 40 Q 4 120, 20 220 T 20 420 T 20 620 T 20 760" stroke="currentColor" className="text-primary" strokeWidth="3" strokeLinecap="round" strokeDasharray="10 8" fill="none" />
            </svg>
          </div>

          {}
          <div className="lg:col-span-4">
            <div ref={polaroid.ref} className="relative mx-auto max-w-xs lg:max-w-none transition-all duration-700 ease-out" style={{
            opacity: polaroid.isVisible ? 1 : 0,
            transform: polaroid.isVisible ? "translateY(0) rotate(3deg)" : "translateY(24px) rotate(0deg)",
            transitionDelay: "240ms"
          }}>
              {}
              <div aria-hidden="true" className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 -rotate-6 bg-primary border border-border" style={{
              width: "110px",
              height: "28px"
            }} />

              <div className="relative p-3 pb-12 bg-card border border-border shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_engraving_facility.png" alt="Industrial laser engraving facility" loading="lazy" decoding="async" className="w-full aspect-[4/5] object-cover" />

                {}
                <div className="absolute -top-3 -right-3 rotate-6 px-3 py-1.5 rounded-lg z-10 bg-primary border border-border shadow-md">
                  <span className="font-serif font-default text-sm tracking-wide text-primary-foreground">
                    OUR SHOP
                  </span>
                </div>

                <p className="absolute bottom-3 left-0 right-0 text-center font-sans italic text-lg text-card-foreground">
                  ready to start →
                </p>
              </div>

              {}
              
            </div>
          </div>
        </div>
      </div>
    </section>;
}